import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMapsLibraries } from "./googleMaps";

const GOOGLE_MAP_ID = "469f567b78eb98c3c5282141";
const MARKER_STYLE_ID = "check-in-google-map-marker-styles";

const DEFAULT_MARKER_COLOR = "#2F6FDB";
const DEFAULT_SOFT_COLOR = "rgba(47, 111, 219, 0.12)";

const ensureMarkerStyles = () => {
    if (document.getElementById(MARKER_STYLE_ID)) {
        return;
    }

    const style = document.createElement("style");
    style.id = MARKER_STYLE_ID;

    style.textContent = `
        @keyframes check-in-marker-pulse {
            0% {
                transform: scale(0.85);
                opacity: 0.55;
            }
            70% {
                transform: scale(1.8);
                opacity: 0;
            }
            100% {
                transform: scale(1.8);
                opacity: 0;
            }
        }

        .check-in-map-marker {
            position: relative;
            width: 24px;
            height: 24px;
            transform: translateY(-12px);
            cursor: pointer;
        }

        .check-in-map-marker__pulse,
        .check-in-map-marker__dot {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: var(--check-in-marker-color);
        }

        .check-in-map-marker__pulse {
            animation: check-in-marker-pulse 1.8s ease-out infinite;
            pointer-events: none;
        }

        .check-in-map-marker__dot {
            inset: 3px;
            border: 3px solid #fff;
            box-shadow: 0 3px 12px rgba(0, 0, 0, 0.28);
        }

        .check-in-map-info {
            width: min(300px, calc(100vw - 48px));
            overflow: hidden;
            border-radius: 14px;
            background: #fff;
            color: #1f2937;
            font-family: Manrope, Helvetica, Arial, sans-serif;
            box-shadow: 0 10px 35px rgba(0, 0, 0, 0.18);
        }

        .check-in-map-info__image {
            display: block;
            width: 100%;
            height: 150px;
            object-fit: cover;
            background: #f1f5f9;
        }

        .check-in-map-info__body {
            padding: 0.9rem;
        }

        .check-in-map-info__name {
            margin: 0;
            color: #111827;
            font-size: 0.95rem;
            font-weight: 800;
            line-height: 1.3;
        }

        .check-in-map-info__address {
            margin: 0.4rem 0 0;
            color: #6b7280;
            font-size: 0.76rem;
            line-height: 1.45;
        }

        .check-in-map-info__category {
            display: inline-flex;
            align-items: center;
            margin-top: 0.65rem;
            padding: 0.3rem 0.55rem;
            border-radius: 999px;
            background: var(--check-in-category-soft-color);
            color: var(--check-in-category-color);
            font-size: 0.7rem;
            font-weight: 800;
        }

        .check-in-map-info__link {
            display: inline-flex;
            align-items: center;
            margin-top: 0.75rem;
            color: #2563eb;
            font-size: 0.75rem;
            font-weight: 800;
            text-decoration: none;
        }

        .check-in-map-info__link:hover {
            text-decoration: underline;
        }

        @media (prefers-reduced-motion: reduce) {
            .check-in-map-marker__pulse {
                display: none;
            }
        }

        @media (max-width: 480px) {
            .check-in-map-info {
                width: min(260px, calc(100vw - 40px));
            }

            .check-in-map-info__image {
                height: 125px;
            }
        }
    `;

    document.head.appendChild(style);
};

const CheckInGoogleMap = ({
    latitude,
    longitude,
    placeId,
    placeName,
    formattedAddress,
    placeImageReference,
    category
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const infoWindowRef = useRef(null);
    const [mapError, setMapError] = useState("");

    useEffect(() => {
        const lat = Number(latitude);
        const lng = Number(longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setMapError("Map location is unavailable.");
            return undefined;
        }

        let mounted = true;

        const loadMap = async () => {
            try {
                setMapError("");

                const libraries = await loadGoogleMapsLibraries();

                if (!mounted || !mapRef.current) {
                    return;
                }

                /*
                 * googleMaps.js returns:
                 * [mapsLibrary, markerLibrary, placesLibrary]
                 */
                const mapsLibrary = libraries[0];
                const markerLibrary = libraries[1];

                if (!mapsLibrary || !markerLibrary) {
                    throw new Error("Google Maps libraries could not be loaded.");
                }

                const { Map, InfoWindow, RenderingType } = mapsLibrary;
                const { AdvancedMarkerElement } = markerLibrary;

                if (!Map || !AdvancedMarkerElement) {
                    throw new Error("Google Maps or Advanced Marker library could not be loaded.");
                }

                ensureMarkerStyles();

                const position = {
                    lat,
                    lng
                };

                const map = new Map(mapRef.current, {
                    center: position,
                    zoom: 16,
                    mapId: GOOGLE_MAP_ID,
                    renderingType: RenderingType?.VECTOR,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    zoomControl: true,
                    gestureHandling: "greedy"
                });

                if (!mounted) {
                    return;
                }

                mapInstanceRef.current = map;

                /*
                 * Custom category marker
                 */
                const markerElement = document.createElement("div");
                const markerColor = category?.color || DEFAULT_MARKER_COLOR;

                markerElement.className = "check-in-map-marker";

                markerElement.setAttribute("role", "button");
                markerElement.setAttribute("aria-label", placeName || "Checked-in location");

                markerElement.style.setProperty("--check-in-marker-color", markerColor);

                markerElement.innerHTML = `
                    <div class="check-in-map-marker__pulse"></div>
                    <div class="check-in-map-marker__dot"></div>
                `;

                const marker = new AdvancedMarkerElement({
                    map,
                    position,
                    title: placeName || "Checked-in location",
                    content: markerElement,
                    gmpClickable: true
                });

                markerRef.current = marker;

                /*
                 * Information bubble
                 */
                if (InfoWindow) {
                    if (!mounted) {
                        return;
                    }

                    const displayName = placeName || "";
                    const address = formattedAddress || "";
                    const imageUrl = placeImageReference || "";
                    const mapQuery = displayName || `${lat},${lng}`;
                    const mapsUrl = new URL("https://www.google.com/maps/search/");

                    mapsUrl.searchParams.set("api", "1");
                    mapsUrl.searchParams.set("query", mapQuery);

                    if (placeId) {
                        mapsUrl.searchParams.set("query_place_id", placeId);
                    }

                    /*
                     * Build DOM nodes instead of injecting provider
                     * data into innerHTML.
                     */
                    const infoContent = document.createElement("div");

                    infoContent.className = "check-in-map-info";

                    infoContent.style.setProperty(
                        "--check-in-category-color",
                        markerColor
                    );

                    infoContent.style.setProperty(
                        "--check-in-category-soft-color",
                        category?.softColor || DEFAULT_SOFT_COLOR
                    );

                    if (imageUrl) {
                        const image = document.createElement("img");

                        image.className = "check-in-map-info__image";
                        image.src = imageUrl;
                        image.alt = displayName ? `${displayName} location` : "Location";
                        image.loading = "lazy";

                        infoContent.appendChild(image);
                    }

                    const body = document.createElement("div");

                    body.className = "check-in-map-info__body";

                    const name = document.createElement("p");

                    name.className = "check-in-map-info__name";
                    name.textContent = displayName;

                    body.appendChild(name);

                    if (address) {
                        const addressElement = document.createElement("p");

                        addressElement.className = "check-in-map-info__address";
                        addressElement.textContent = address;

                        body.appendChild(addressElement);
                    }

                    if (category?.label) {
                        const categoryElement = document.createElement("span");

                        categoryElement.className = "check-in-map-info__category";
                        categoryElement.textContent = category.label;

                        body.appendChild(categoryElement);
                    }

                    const link = document.createElement("a");

                    link.className = "check-in-map-info__link";
                    link.href = mapsUrl.toString();
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.textContent = "View on Google Maps";

                    body.appendChild(link);

                    infoContent.appendChild(body);

                    const infoWindow = new InfoWindow({
                        content: infoContent
                    });

                    infoWindowRef.current = infoWindow;

                    /*
                     * Marker click / tap.
                     */
                    marker.addListener("click", () => {
                        infoWindow.open({
                            map,
                            anchor: marker
                        });
                    });

                    /*
                     * Close the information bubble when the map
                     * itself is clicked.
                     */
                    map.addListener("click", () => {
                        infoWindow.close();
                    });
                }

                /*
                 * Make sure the map remains centered after
                 * the modal finishes rendering.
                 */
                requestAnimationFrame(() => {
                    if (mounted && mapRef.current) {
                        map.setCenter(position);
                    }
                });
            } catch (error) {
                console.error("Google Maps failed to load:", error);

                if (mounted) {
                    setMapError(error?.message || "Unable to load the map.");
                }
            }
        };

        loadMap();

        return () => {
            mounted = false;

            if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
            }

            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }

            mapInstanceRef.current = null;
        };
    }, [
        latitude,
        longitude,
        placeId,
        placeName,
        formattedAddress,
        placeImageReference,
        category
    ]);

    if (mapError) {
        return (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "320px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1rem",
                    color: "#6b7280",
                    background: "#f8fafc",
                    textAlign: "center"
                }}
            >
                {mapError}
            </div>
        );
    }

    return (
        <div
            ref={mapRef}
            style={{
                width: "100%",
                height: "100%",
                minHeight: "320px"
            }}
            aria-label={placeName ? `Map showing ${placeName}` : "Check-in location map"}
        />
    );
};

export default CheckInGoogleMap;
